import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentUser, refreshToken, clearAuth } from '../store/slices/authSlice';
import { selectAuth } from '../store/slices/authSlice';
import authAPI from '../services/authAPI';
import { MastersService } from '../services/masters';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const [isInitialized, setIsInitialized] = useState(false);
  const [tokenRefreshInterval, setTokenRefreshInterval] = useState(null);
  const lastImpersonationTokenRef = useRef(null);
  const lastUserKeyRef = useRef(null);

  // Initialize authentication state using session cookies
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await dispatch(getCurrentUser()).unwrap();
      } catch (error) {
        // If session is not present, ensure clean auth state
        dispatch(clearAuth());
      } finally {
        setIsInitialized(true);
      }
    };
    initializeAuth();
  }, [dispatch]);

  // Keep UI auth state in sync with tab-scoped impersonation token.
  // Some pages set/remove `sessionStorage.impersonation_token` without triggering a full reload.
  useEffect(() => {
    lastImpersonationTokenRef.current = sessionStorage.getItem('impersonation_token');

    const interval = setInterval(async () => {
      const currentToken = sessionStorage.getItem('impersonation_token');
      if (currentToken === lastImpersonationTokenRef.current) return;
      lastImpersonationTokenRef.current = currentToken;
      try {
        await dispatch(getCurrentUser()).unwrap();
      } catch (_) {
        dispatch(clearAuth());
      }
    }, 1500);

    return () => clearInterval(interval);
  }, [dispatch]);

  // Set up automatic token refresh
  const setupTokenRefresh = () => {
    // Clear existing interval
    if (tokenRefreshInterval) {
      clearInterval(tokenRefreshInterval);
    }

    // Refresh token every 6 hours (token expires in 7 days)
    const interval = setInterval(async () => {
      try {
        await dispatch(refreshToken()).unwrap();
      } catch (error) {
        console.error('Token refresh failed:', error);
        dispatch(clearAuth());
      }
    }, 6 * 60 * 60 * 1000); // 6 hours

    setTokenRefreshInterval(interval);
  };

  // Clear token refresh interval on logout
  useEffect(() => {
    if (!auth.isAuthenticated && tokenRefreshInterval) {
      clearInterval(tokenRefreshInterval);
      setTokenRefreshInterval(null);
    }
  }, [auth.isAuthenticated, tokenRefreshInterval]);

  useEffect(() => {
    if (!isInitialized) return;
    const userKey = String(auth.user?.id ?? auth.user?.user_id ?? auth.user?.userId ?? '').trim() || null;

    if (!auth.isAuthenticated) {
      lastUserKeyRef.current = null;
      MastersService.clearCoursesCache?.();
      return;
    }

    if (lastUserKeyRef.current && lastUserKeyRef.current !== userKey) {
      MastersService.clearCoursesCache?.();
    }
    lastUserKeyRef.current = userKey;
    // No global course prefetch here: it pulled the entire course catalogue on
    // every page (homepage included) for signed-in users. The courses page
    // fetches and caches it itself on first visit.
  },[auth.isAuthenticated, auth.user, isInitialized]);

  // Update API token when auth state changes (JWT flows only)
  useEffect(() => {
    if (auth.token) {
      authAPI.setAuthToken(auth.token);
    } else {
      authAPI.removeAuthToken();
    }
  }, [auth.token]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
      }
    };
  }, [tokenRefreshInterval]);

  // Helper functions
  const getUserRole = () => {
    const u = auth.user || {};

    const normalizedRole = String(u.role || '').toLowerCase();
    const normalizedRoleType = String(u.role_type || '').toLowerCase();
    const normalizedUserType = String(u.user_type || '').toLowerCase();

    if (normalizedRole === 'student' || normalizedRoleType === 'student' || normalizedUserType === 'student') {
      return 'student';
    }

    if (normalizedRoleType === 'superadmin') {
      return 'admin';
    }

    if (!u.vendor_id && normalizedUserType === 'admin') {
      return 'admin';
    }

    if (u.vendor_id) {
      return 'vendor';
    }

    // 1. Check for specific role_type if present and valid (prioritize admin/staff access)
    // This allows custom roles like "Tele caller Sales" to be treated as admin/staff
    if (normalizedRoleType === 'admin' || normalizedUserType === 'staff') {
      return 'admin'; // Treat superadmin team as admin
    }
    
    // Explicitly check role name for vendors
    if (u.role === 'Vendor Admin' || u.role === 'Vendor Staff') {
      return 'vendor';
    }
    
    const rawPrimary =
      u.role ||
      u.user_type ||
      u.userType ||
      null;
    const rawFromArray = Array.isArray(u.roles)
      ? (u.roles.includes('admin') ? 'admin' :
         u.roles.includes('instructor') ? 'instructor' :
         u.roles.includes('student') ? 'student' :
         u.roles[0] || null)
      : null;
    const rawNumeric = u.role_id || u.type_id || null;
    const byFlags = u.is_admin ? 'admin' : (u.is_instructor ? 'instructor' : null);
    const candidate = rawPrimary || rawFromArray || byFlags || rawNumeric;
    if (typeof candidate === 'number') {
      if (candidate === 1) return 'admin';
      if (candidate === 2) return 'instructor';
      if (candidate === 3) return 'student';
      return null;
    }
    if (typeof candidate === 'string') {
      const v = candidate.toLowerCase();
      if (v === 'administrator') return 'admin';
      if (v === 'superadmin') return 'admin';
      if (v === 'teacher') return 'instructor';
      return v;
    }
    return candidate;
  };
  
  const hasRole = (role) => {
    const current = getUserRole();
    return current === String(role).toLowerCase();
  };

  const hasPermission = (key, action = 'view') => {
    const currentRole = getUserRole();
    if (currentRole === 'student') return true;
    const value = auth.user?.permissions?.[key]?.[action];
    return value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';
  };

  const hasAnyRole = (roles) => {
    const current = getUserRole();
    const normalized = roles.map((r) => String(r).toLowerCase());
    console.log('[DEBUG-AUTH] hasAnyRole check. Required:', normalized, 'Current:', current, 'User:', auth.user);
    if (normalized.includes(current)) return true;
    const list = (auth.user?.roles || []).map((r) => (typeof r === 'string' ? r.toLowerCase() : r));
    return Array.isArray(list) && normalized.some((r) => list.includes(r));
  };

  const isAdmin = () => hasRole('admin');
  const isInstructor = () => hasRole('instructor');
  const isStudent = () => hasRole('student');

  const canAccessAdminPanel = () => isAdmin();
  const canAccessInstructorPanel = () => isAdmin() || isInstructor();

  const canCreateCourse = () => isAdmin() || isInstructor();
  const canEditCourse = (courseInstructorId) => {
    return isAdmin() || (isInstructor() && auth.user?.id === courseInstructorId);
  };

  const canDeleteCourse = (courseInstructorId) => {
    return isAdmin() || (isInstructor() && auth.user?.id === courseInstructorId);
  };

  const canManageUsers = () => isAdmin();

  const canViewAnalytics = () => isAdmin() || isInstructor();

  const isEmailVerified = () => {
    return auth.user?.email_verified === true;
  };

  const getUserDisplayName = () => {
    if (!auth.user) return 'Guest';
    const full = `${auth.user.first_name || ''} ${auth.user.last_name || ''}`.trim();
    if (full) return full;
    const phone = auth.user.phone || auth.user.phone_number || auth.user.number || '';
    if (phone) return phone;
    return auth.user.email || 'Student';
  };

  const getUserInitials = () => {
    if (!auth.user) return 'G';
    const firstName = auth.user.first_name || '';
    const lastName = auth.user.last_name || '';
    const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    if (initials.trim()) return initials;
    const phone = auth.user.phone || auth.user.phone_number || auth.user.number || '';
    if (phone && typeof phone === 'string' && phone.length) return phone[0].toUpperCase();
    const email = auth.user.email || '';
    return email ? email.charAt(0).toUpperCase() : 'S';
  };

  const value = {
    // Auth state
    ...auth,
    isInitialized,
    
    // Helper functions
    hasRole,
    hasAnyRole,
    hasPermission,
    isAdmin,
    isInstructor,
    isStudent,
    canAccessAdminPanel,
    canAccessInstructorPanel,
    canCreateCourse,
    canEditCourse,
    canDeleteCourse,
    canManageUsers,
    canViewAnalytics,
    isEmailVerified,
    getUserDisplayName,
    getUserInitials,
    
    // Actions
    setupTokenRefresh,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
